import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonIcon,
  Modal,
  ModalProps,
  Toggle,
  TrashIcon,
} from '@rabbit/elements/shared-components';
import {
  nestApiCreateTenantModels,
  nestApiUpdateTenantModels,
  nestApiDeleteTenantModels,
} from '@rabbit/bizproc/core';
import { TenantMakesModels } from '@rabbit/data/types';
import { Input } from '@rabbit/elements/shared-components';
import { useEffect, useMemo, useState, useRef, useContext } from 'react';
import { CreateTable } from '@rabbit/sage/utils/CreateTable';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { TenantModelsCreateDto, TenantModelsUpdateDto } from '@rabbit/nest/api';
import { UserContext } from '@rabbit/sage/context/UserContext';
import { toast } from 'react-toastify';

interface ModelListShape {
  docid: string;
  id: string;
  value: string;
  label: string;
  name: string;
  enabled: boolean;
  description: string;
}

export default function ModelTab() {
  const { t } = useTranslation();
  const formRef = useRef(null) as any;
  const tenantLink = t('tenantLink');
  const { activeTenantInfo } = useContext(UserContext) || {};
  const [isLoading, setIsLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<TenantMakesModels | null>(
    null
  );
  const [modelList, setModelList] = useState<TenantMakesModels[]>([]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        // For now, we'll need to get models from the tenant document
        // This would require a new API endpoint to get tenant models
        // For now, we'll set an empty array
        setModelList(activeTenantInfo?.models || []);
      } catch (error) {
        console.error('Failed to fetch models:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchModels();
  }, [t]);

  const modalSettings: ModalProps = {
    kind: 'generic' as const,
    settings: {
      title: selectedModel ? t('general.editModel') : t('general.addModel'),
      text: selectedModel ? t('general.editModel') : t('general.addModel'),
      handleClose: () => {
        setEditModalOpen(false);
        setSelectedModel(null);
      },
    },
  };

  const deleteModalSettings: ModalProps = {
    kind: 'generic' as const,
    settings: {
      title: t('Delete Model'),
      text: t('Are you sure you want to delete this model?'),
      primaryButtonText: t('general.delete'),
      outlineButtonText: t('general.cancel'),
      handleClose: () => {
        setDeleteModalOpen(false);
        setSelectedModel(null);
      },
      handleOutlineClick: () => {
        setDeleteModalOpen(false);
        setSelectedModel(null);
      },
      handlePrimaryClick: async () => {
        if (selectedModel) {
          await handleDelete(selectedModel.docid);
          setDeleteModalOpen(false);
          setSelectedModel(null);
        }
      },
    },
  };

  const schema = Yup.object({
    label: Yup.string().required(t('message.pleaseEnterAName')),
    description: Yup.string().optional(),
    enabled: Yup.boolean().optional().default(true),
  });

  const initialValues = {
    label: selectedModel?.label || '',
    description: selectedModel?.description || '',
    enabled: selectedModel?.enabled || true,
  };

  const handleSubmit = async (values: {
    label: string;
    description: string;
    enabled: boolean;
  }) => {
    try {
      if (selectedModel) {
        await handleUpdate(selectedModel.docid, {
          tenantLink,
          model: {
            docid: selectedModel.docid,
            label: values.label,
            description: values.description,
            enabled: values.enabled,
          },
        });
      } else {
        await handleCreate({
          tenantLink,
          models: {
            docid: `model_${Date.now()}`, // Generate a unique docid
            label: values.label,
            description: values.description,
            enabled: true,
          },
        });
      }
    } catch (error) {
      console.error('Failed to submit form:', error);
    }
  };

  const handleEditModal = (itemDetails?: ModelListShape) => {
    if (itemDetails) {
      setSelectedModel(
        modelList.find((m) => m.docid === itemDetails.docid) || null
      );
    } else {
      setSelectedModel(null);
    }
    setEditModalOpen(true);
  };

  const handleCreate = async (data: TenantModelsCreateDto) => {
    try {
      const response: any = await nestApiCreateTenantModels(data);
      if (response && response.models) {
        setModelList(response.models);
      }
      setEditModalOpen(false);
    } catch (error: any) {
      if (error.status === 403) {
        toast.error(t('general.youAreNotAuthorizedToCreateModels'));
      } else {
        console.error('Failed to create model:', error.message);
        toast.error(t('message.somethingWentWrongPleaseTryAgain'));
      }
    }
  };

  const handleUpdate = async (docid: string, data: TenantModelsUpdateDto) => {
    try {
      const response: any = await nestApiUpdateTenantModels(data);
      if (response && response.models) {
        setModelList(response.models);
      }
      setEditModalOpen(false);
    } catch (error: any) {
      if (error.status === 403) {
        toast.error(t('general.youAreNotAuthorizedToCreateModels'));
      } else {
        console.error('Failed to update model:', error.message);
        toast.error(t('message.somethingWentWrongPleaseTryAgain'));
      }
    }
  };

  const handleDelete = async (docid: string) => {
    try {
      const response: any = await nestApiDeleteTenantModels({
        tenantLink,
        docid,
      });
      if (response && response.models) {
        toast.success(t('general.modelDeletedSuccessfully'));
        setModelList(response.models);
      }
    } catch (error: any) {
      if (error.status === 403) {
        toast.error(t('general.youAreNotAuthorizedToCreateModels'));
      } else {
        console.error('Failed to delete model:', error.message);
        toast.error(t('message.somethingWentWrongPleaseTryAgain'));
      }
    }
  };

  const ModelTable = useMemo(() => {
    const modelListOptions: ModelListShape[] =
      modelList?.map((model) => ({
        docid: model.docid,
        id: model.docid,
        value: model.docid,
        label: model.label,
        name: model.label,
        enabled: model.enabled,
        description: model.description || '-',
      })) || [];
    console.log('modelListOptions', modelListOptions, modelList);

    return CreateTable<ModelListShape>(modelListOptions, [
      {
        header: t('general.name'),
        accessorKey: 'label',
        value: (model) => model.label,
      },
      {
        header: t('general.description'),
        accessorKey: 'description',
        value: (model) => model.description,
      },
      {
        header: t('general.enabled'),
        accessorKey: 'enabled',
        value: (model) => (model.enabled ? t('general.yes') : t('general.no')),
      },
      {
        header: '',
        accessorKey: 'actions',
        value: (model) => (
          <div className="space-x-2 text-center">
            <ButtonIcon
              label={t('general.editModel')}
              kind={'bgLightGreen'}
              iconLeft={true}
              Icon={PencilSquareIcon}
              onClick={() => {
                setSelectedModel(
                  modelList.find((m) => m.docid === model.docid) || null
                );
                handleEditModal(model);
              }}
            />
            <ButtonIcon
              label={t('general.deleteModel')}
              kind={'bgRed'}
              iconLeft={true}
              Icon={TrashIcon}
              onClick={() => {
                setSelectedModel(
                  modelList.find((m) => m.docid === model.docid) || null
                );
                setDeleteModalOpen(true);
              }}
            />
          </div>
        ),
        size: 15,
        maxSize: 30,
      },
    ]);
  }, [modelList, isLoading, t]);

  return (
    <>
      <div className="relative z-10 flex justify-end py-4">
        <div className="md:absolute md:right-5 md:top-[76px] xl:top-[60px]">
          <ButtonIcon
            label={t('general.addModel')}
            iconLeft={true}
            onClick={() => handleEditModal()}
          />
        </div>
      </div>
      <ModelTable
        initialState={{
          columnPinning: { right: ['actions'] },
          showGlobalFilter: true,
          isLoading,
        }}
        muiSearchTextFieldProps={{
          placeholder: t('general.searchNameOrDescription'),
          sx: {
            minWidth: '600px',
            borderRadius: '20px',
            backgroundColor: '#fff',
            margin: '5px 0',
            '& .MuiOutlinedInput-root': {
              '& > fieldset': { borderRadius: '10px' },
            },
          },
          variant: 'outlined',
          margin: 'none',
        }}
      />
      {editModalOpen && (
        <Modal
          className="md:w-[500px] lg:w-[724px]"
          kind={'generic'}
          settings={modalSettings.settings}
        >
          <Formik
            onSubmit={handleSubmit}
            initialValues={selectedModel || initialValues}
            validationSchema={schema}
            validateOnChange={true}
            validateOnBlur={false}
            innerRef={formRef}
          >
            {(props) => (
              <Form className="mt-2 w-full px-5">
                <div className="w-full py-2">
                  <Input
                    type="text"
                    label={`${t('general.name')}*`}
                    name="label"
                    settings={{
                      id: 'label',
                      hint: '*required',
                      placeholder: t('general.modelName'),
                    }}
                  />
                </div>
                <div className="w-full py-2">
                  <Input
                    type="textarea"
                    label={`${t('general.description')}`}
                    name="description"
                    settings={{
                      id: 'description',
                      placeholder: t('general.description'),
                    }}
                  />
                </div>

                <div className="mt-5 flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2">
                  <div className="grow capitalize">{t('general.enabled')}</div>
                  <Toggle
                    name="enabled"
                    checked={props.values.enabled}
                    onChange={() => {
                      props.setFieldValue('enabled', !props.values.enabled);
                    }}
                  />
                </div>

                <div className="mt-8 flex gap-5 py-2">
                  <Button
                    kind={'primary'}
                    type="submit"
                    disabled={props.isSubmitting || !props.isValid}
                  >
                    {t('general.save')}
                  </Button>
                  <Button kind={'red'} onClick={() => setEditModalOpen(false)}>
                    {t('general.cancel')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal>
      )}
      {deleteModalOpen && (
        <Modal
          className="h-fit min-w-[350px] max-w-[500px] rounded-md bg-white"
          kind={'pop-up'}
          settings={deleteModalSettings.settings}
        />
      )}
    </>
  );
}
